import {
  isPointDebugEnabled,
  maskPointIdentifier,
  pointDebug,
  pointDebugError,
  pointDebugWarn,
  summarizePointResponse,
} from '../utils/pointDebug'

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD
    ? 'https://adam.lenkostudio.com'
    : '')

export interface PointApiResponse {
  wallet: {
    pointsTotal: number
    coinsTotal: number
    pointsResetAt: string | null
    pointsCycleStartedAt: string | null
    expirationDays: number
    maxPointsBalance: number
  }
  dailyReview: {
    eligible: boolean
    ineligibleReason: string | null
    rewardPoints: number
    requiredActiveSeconds: number
    activeSeconds: number
    requiredMeaningfulActions: number
    meaningfulActions: number
    rewardedToday: boolean
    rewardedDaysThisCycle: number
    maxRewardsPerCycle: number
  }
  rewardClaimed?: boolean
  rewardGranted?: boolean
  pointsAwarded?: number
  capReached?: boolean
}

export type PointHeartbeatMode = 'baseline' | 'active'

export type MeaningfulActionCode =
  | 'dashboard.viewed'
  | 'video.detail_opened'

let requestSequence = 0

function safeRequestBody(body?: BodyInit | null) {
  if (!body || typeof body !== 'string') return null

  try {
    const parsed = JSON.parse(body) as Record<string, unknown>

    return {
      accountId: typeof parsed.accountId === 'string'
        ? maskPointIdentifier(parsed.accountId)
        : null,
      mode: typeof parsed.mode === 'string' && (parsed.mode === 'baseline' || parsed.mode === 'active')
        ? parsed.mode
        : 'active',
      actionCode: typeof parsed.actionCode === 'string'
        ? parsed.actionCode
        : null,
      relatedEntityId: typeof parsed.relatedEntityId === 'string'
        ? maskPointIdentifier(parsed.relatedEntityId)
        : null,
    }
  } catch {
    return { bodyPresent: true }
  }
}

function safeErrorPayload(data: unknown) {
  if (!data || typeof data !== 'object') return null

  const value = data as Record<string, unknown>

  return {
    error: typeof value.error === 'string' ? value.error : null,
    message: typeof value.message === 'string' ? value.message : null,
    code: typeof value.code === 'string' ? value.code : null,
  }
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const requestId = ++requestSequence
  const method = options?.method || 'GET'
  const startedAt = performance.now()

  pointDebug('http.request', {
    requestId,
    method,
    endpoint,
    body: safeRequestBody(options?.body),
    pageVisibility: document.visibilityState,
  })

  let response: Response

  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers || {}),
      },
      credentials: 'include',
    })
  } catch (err) {
    pointDebugError('http.network_error', {
      requestId,
      method,
      endpoint,
      elapsedMs: Math.round(performance.now() - startedAt),
      error: err instanceof Error ? err.message : String(err),
    })
    throw err
  }

  let data: unknown = null

  try {
    data = await response.json()
  } catch (err) {
    pointDebugError('http.invalid_json', {
      requestId,
      method,
      endpoint,
      status: response.status,
      elapsedMs: Math.round(performance.now() - startedAt),
      error: err instanceof Error ? err.message : String(err),
    })
    throw new Error(`Point API returned invalid JSON (${response.status})`)
  }

  const elapsedMs = Math.round(performance.now() - startedAt)

  if (!response.ok) {
    const errorPayload = safeErrorPayload(data)
    pointDebugWarn('http.error_response', {
      requestId,
      method,
      endpoint,
      status: response.status,
      elapsedMs,
      response: errorPayload,
    })

    const value = data as Record<string, unknown>
    const message =
      (typeof value?.message === 'string' ? value.message : null) ||
      (typeof value?.error === 'string' ? value.error : null) ||
      `Request failed (${response.status})`

    throw new Error(message)
  }

  const pointResponse = data as PointApiResponse
  pointDebug('http.response', {
    requestId,
    method,
    endpoint,
    status: response.status,
    elapsedMs,
    point: summarizePointResponse(pointResponse),
  })

  if (pointResponse?.rewardGranted || (pointResponse?.pointsAwarded ?? 0) > 0) {
    pointDebug('reward.achievement', {
      requestId,
      endpoint,
      pointsAwarded: pointResponse.pointsAwarded ?? 0,
      pointsTotal: pointResponse.wallet?.pointsTotal ?? null,
      rewardedToday: pointResponse.dailyReview?.rewardedToday ?? null,
    })
  }

  if (isPointDebugEnabled()) {
    console.debug(`[ADAM Points] request #${requestId} completed`)
  }

  return data as T
}

export const pointActivityService = {
  getStatus(accountId: string) {
    pointDebug('service.getStatus', {
      accountId: maskPointIdentifier(accountId),
    })

    return fetchApi<PointApiResponse>(
      `/api/points/account/${encodeURIComponent(accountId)}`
    )
  },

  recordHeartbeat(accountId: string, mode: PointHeartbeatMode = 'active') {
    pointDebug('service.recordHeartbeat', {
      accountId: maskPointIdentifier(accountId),
      mode,
    })

    return fetchApi<PointApiResponse>('/api/points/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ accountId, mode }),
    })
  },

  recordMeaningfulAction(
    accountId: string,
    actionCode: MeaningfulActionCode,
    relatedEntityId?: string | null
  ) {
    pointDebug('service.recordMeaningfulAction', {
      accountId: maskPointIdentifier(accountId),
      actionCode,
      relatedEntityId: maskPointIdentifier(relatedEntityId),
    })

    return fetchApi<PointApiResponse>('/api/points/meaningful-action', {
      method: 'POST',
      body: JSON.stringify({
        accountId,
        actionCode,
        relatedEntityId: relatedEntityId ?? null,
      }),
    })
  },
}
